/**
 * Client-side time estimator for AI image generation.
 *
 * The backend `POST /api/v1/image/generate` uses RunPod `runsync` (blocking)
 * with no progress streaming, so the UI has no real source of progress until
 * the response arrives. This estimator gives us a smooth 0→95% curve based on
 * provider + resolution + step count + image count.
 *
 * Routing reality (matters because cold-start dominates user-perceived time):
 *   - `fooocus`     -> our own RunPod worker; scales-to-zero -> can cold-start.
 *   - `comfyui-sd`  -> local GPU; first request loads the model into VRAM.
 *   - everything else -> third-party public RunPod endpoints kept warm by
 *     their owners, so we don't add a cold-start buffer.
 *
 * Coefficients are calibrated to typical staging/production runs (mid-range
 * GPU, default settings) and intentionally lean slightly conservative — it's
 * better for the bar to creep past expected than to sit at 100% waiting.
 */

import type { AIImageProvider } from '../constants/imageSource';

export interface ImageEstimateInput {
  provider: AIImageProvider;
  numImages: number;
  numSteps?: number;
  width: number;
  height: number;
  performance?: string;
}

export interface ImageEstimateResult {
  /** Expected wall-clock seconds, including warm-up buffer when applicable. */
  expectedSeconds: number;
  /** True when the provider may cold-start (Fooocus RunPod / local GPU model load). */
  mayColdStart: boolean;
}

const MEGAPIXEL = 1024 * 1024;

// Base seconds per megapixel per image at the provider's reference step count.
// OpenRouter values are wall-clock seconds per call (resolution-agnostic) — the
// MP scaling is mostly noise vs API + queue latency, so we use a flat baseline.
const PROVIDER_BASE_SECONDS_PER_MP: Record<AIImageProvider, number> = {
  fooocus: 8,
  'comfyui-sd': 4,
  'z-image-turbo': 2.5,
  'black-forest-labs-flux-1-schnell': 3,
  'p-image-t2i': 4,
  'seedream-v4-t2i': 6,
  'qwen-image-t2i': 6,
  'qwen-image-t2i-lora': 8,
  'wan-2-6-t2i': 6,
  'black-forest-labs-flux-1-dev': 12,
  'openrouter-nano-banana-pro': 26,
  'openrouter-nano-banana-flash': 18,
  'openrouter-gpt-54-image-2': 158,
  'openrouter-nano-banana-25': 8,
  'openrouter-gpt-5-image-mini': 53,
  'openrouter-gpt-5-image': 85,
};

// Step count baked into PROVIDER_BASE_SECONDS_PER_MP. 0 = provider doesn't
// expose num_steps, so we don't scale by it.
const PROVIDER_REFERENCE_STEPS: Record<AIImageProvider, number> = {
  fooocus: 30,
  'comfyui-sd': 20,
  'z-image-turbo': 0,
  'black-forest-labs-flux-1-schnell': 4,
  'p-image-t2i': 0,
  'seedream-v4-t2i': 0,
  'qwen-image-t2i': 0,
  'qwen-image-t2i-lora': 0,
  'wan-2-6-t2i': 0,
  'black-forest-labs-flux-1-dev': 50,
  'openrouter-nano-banana-pro': 0,
  'openrouter-nano-banana-flash': 0,
  'openrouter-gpt-54-image-2': 0,
  'openrouter-nano-banana-25': 0,
  'openrouter-gpt-5-image-mini': 0,
  'openrouter-gpt-5-image': 0,
};

// Our own RunPod worker — scales to zero, so cold-start is real.
const RUNPOD_COLD_START_PROVIDERS = new Set<AIImageProvider>(['fooocus']);
// Local GPU — first request pays the model-load tax.
const LOCAL_PROVIDERS = new Set<AIImageProvider>(['comfyui-sd']);

const RUNPOD_COLD_START_SECONDS = 35;
const LOCAL_WARMUP_SECONDS = 8;

const MIN_EXPECTED_SECONDS = 3;
const FALLBACK_BASE_SECONDS_PER_MP = 6;
const MIN_AREA_MP = 0.25;

function performanceMultiplier(performance?: string): number {
  switch (performance) {
    case 'Extreme Speed':
      return 0.5;
    case 'Lightning':
    case 'Hyper-SD':
      return 0.35;
    case 'Quality':
      return 1.4;
    default:
      return 1.0;
  }
}

export function estimateImageGenerationSeconds(input: ImageEstimateInput): ImageEstimateResult {
  const base = PROVIDER_BASE_SECONDS_PER_MP[input.provider] ?? FALLBACK_BASE_SECONDS_PER_MP;
  const refSteps = PROVIDER_REFERENCE_STEPS[input.provider] ?? 0;

  const areaMp = Math.max(MIN_AREA_MP, (input.width * input.height) / MEGAPIXEL);

  let stepFactor = 1.0;
  if (refSteps > 0 && typeof input.numSteps === 'number' && input.numSteps > 0) {
    stepFactor = input.numSteps / refSteps;
  }

  const perfFactor = performanceMultiplier(input.performance);
  const perImageSeconds = base * areaMp * stepFactor * perfFactor;
  let total = perImageSeconds * Math.max(1, input.numImages);

  const isRunpodCold = RUNPOD_COLD_START_PROVIDERS.has(input.provider);
  const isLocal = LOCAL_PROVIDERS.has(input.provider);

  if (isRunpodCold) total += RUNPOD_COLD_START_SECONDS;
  else if (isLocal) total += LOCAL_WARMUP_SECONDS;

  return {
    expectedSeconds: Math.max(MIN_EXPECTED_SECONDS, Math.round(total)),
    mayColdStart: isRunpodCold || isLocal,
  };
}

export function parseResolution(resolution: string): { width: number; height: number } {
  const [w, h] = resolution.split('x');
  const width = Number.parseInt(w ?? '', 10);
  const height = Number.parseInt(h ?? '', 10);
  return {
    width: Number.isFinite(width) && width > 0 ? width : 1024,
    height: Number.isFinite(height) && height > 0 ? height : 1024,
  };
}
